import { useMemo } from "react";
import { useBook } from "@/providers/use-book";
import type { BookRowVariant } from "./book-row-gutter";
import type { ItemWithInsured } from "./use-pinned-insureds";

export interface BookItemForInsured {
  item: ItemWithInsured<unknown>;
  variant: BookRowVariant;
}

/**
 * The row the Book is already showing for an insured, if it is showing one.
 *
 * Read off the Book's own lists rather than the insured's opportunities, because
 * the point is to match what is on screen. The Book is filtered — by year, by
 * `showExpired`, by broker scope — so an opportunity the insured owns is not
 * necessarily a row in the column, and describing the insured with one that is
 * not there would be less consistent, not more.
 *
 * First match wins, scanning tabs in the order `Book` renders them. An insured
 * with a plan and a quote genuinely has two rows down there; the Book itself
 * picks no canonical one, so neither can this.
 */
export const useBookItemForInsured = (insuredId: string | null | undefined): BookItemForInsured | null => {
  const {
    bindRequested,
    bound,
    brokerReview,
    delivered,
    informationGathering,
    lost,
    pending,
    quoted,
    quotedRenewals,
    submitted,
    submittedRenewals,
    solicitedUnsubmittedRenewals,
    unsolicitedUnsubmittedRenewals,
    unsubmitted,
    upcomingRenewals,
    won,
  } = useBook();

  return useMemo(() => {
    if (!insuredId) {
      return null;
    }

    const sections: { items: ItemWithInsured<unknown>[]; variant: BookRowVariant }[] = [
      { items: informationGathering, variant: "default" },
      { items: brokerReview, variant: "default" },
      { items: unsubmitted, variant: "default" },
      { items: submitted, variant: "default" },
      { items: quoted, variant: "default" },
      { items: upcomingRenewals, variant: "renewal" },
      { items: unsolicitedUnsubmittedRenewals, variant: "renewal" },
      { items: solicitedUnsubmittedRenewals, variant: "renewal" },
      { items: submittedRenewals, variant: "renewal" },
      { items: quotedRenewals, variant: "renewal" },
      { items: pending, variant: "default" },
      { items: delivered, variant: "default" },
      { items: bindRequested, variant: "default" },
      { items: bound, variant: "default" },
      { items: Object.values(won).flat(), variant: "default" },
      { items: Object.values(lost).flat(), variant: "default" },
    ];

    for (const { items, variant } of sections) {
      const item = items.find((candidate) => candidate.insured?.id === insuredId);

      if (item) {
        return { item, variant };
      }
    }

    return null;
  }, [
    bindRequested,
    bound,
    brokerReview,
    delivered,
    informationGathering,
    insuredId,
    lost,
    pending,
    quoted,
    quotedRenewals,
    solicitedUnsubmittedRenewals,
    submitted,
    submittedRenewals,
    unsolicitedUnsubmittedRenewals,
    unsubmitted,
    upcomingRenewals,
    won,
  ]);
};
